import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Clock, Award, Sparkles, Calendar } from 'lucide-react';
import { getServices, getOffers } from '../services/api';
import { testimonials } from '../data/mockData';
import SEOComponent from '../components/SEOComponent';

const Home = () => {
  const [services, setServices] = useState([]);
  const [specialOffers, setSpecialOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, offersData] = await Promise.all([
          getServices(),
          getOffers()
        ]);
        setServices(servicesData);
        setSpecialOffers(offersData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const featuredServices = services.slice(0, 6);

  if (loading) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center' }}>
        <p className="body-large">Chargement...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section 
        style={{
          position: 'relative',
          height: '90vh',
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_beauty-institut-nina/artifacts/d4uibxiz_F381479D-3538-467C-960B-DECA3DE28447.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.75)'
          }}
        />
        
        <div className="container" style={{ position: 'relative', zIndex: 2, maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ maxWidth: '700px' }}>
            <h1 
              className="hero-large" 
              style={{ 
                color: 'var(--bg-primary)', 
                marginBottom: '24px',
                fontSize: window.innerWidth < 768 ? '32px' : '48px'
              }}
            >
              Soins esthétiques d'exception à Villemoisson-sur-Orge
            </h1>
            <p 
              className="body-large" 
              style={{ 
                color: 'var(--bg-primary)', 
                marginBottom: '40px',
                lineHeight: '1.7'
              }}
            >
              Découvrez un institut de beauté dédié à votre bien-être, où expertise et élégance se rencontrent pour révéler votre beauté naturelle.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Link to="/services" className="btn-primary" style={{ background: 'var(--interactive-base)', color: 'var(--bg-primary)', borderColor: 'var(--bg-primary)' }}>
                Découvrir nos services
              </Link>
              <a 
                href="https://www.planity.com/nina-care-institut-91360-villemoisson-sur-orge" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ background: 'transparent', color: 'var(--bg-primary)', borderColor: 'var(--bg-primary)' }}
              >
                Réserver en ligne
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Offres du mois */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
              <Sparkles size={28} color="#C9A961" />
              <h2 className="hero-medium" style={{ margin: 0 }}>Offres du Mois</h2>
              <Sparkles size={28} color="#C9A961" />
            </div>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Profitez de nos promotions exclusives à durée limitée
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth >= 1024 ? 'repeat(3, 1fr)' : window.innerWidth >= 768 ? 'repeat(2, 1fr)' : '1fr',
            gap: '32px'
          }}>
            {specialOffers.map((offer) => (
              <div 
                key={offer.id}
                className="hover-lift"
                style={{
                  position: 'relative',
                  background: 'var(--bg-primary)',
                  border: offer.isHighlight ? '2px solid #C9A961' : '1px solid var(--border-light)',
                  borderRadius: '0px',
                  overflow: 'hidden',
                  boxShadow: offer.isHighlight ? '0 8px 24px rgba(201, 169, 97, 0.15)' : 'none'
                }}
              >
                {/* Badge */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: offer.isHighlight ? '#C9A961' : 'var(--interactive-base)',
                    color: 'var(--bg-primary)',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    zIndex: 2,
                    borderRadius: '0px'
                  }}
                >
                  {offer.badge}
                </div>

                {/* Discount Badge */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    background: '#ba3e2b',
                    color: 'var(--bg-primary)',
                    padding: '8px 16px',
                    fontSize: '16px',
                    fontWeight: 700,
                    zIndex: 2,
                    borderRadius: '0px'
                  }}
                >
                  {offer.discount}
                </div>

                {/* Image */}
                <div style={{ 
                  position: 'relative',
                  height: '220px',
                  overflow: 'hidden'
                }}>
                  <img 
                    src={offer.image} 
                    alt={offer.title}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      filter: 'brightness(0.85)'
                    }}
                  />
                </div>

                {/* Content */}
                <div style={{ padding: '24px' }}>
                  <p className="body-small" style={{ color: '#C9A961', marginBottom: '8px', fontWeight: 700 }}>
                    {offer.subtitle}
                  </p>
                  <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                    {offer.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                    {offer.description}
                  </p>

                  {/* Prix */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'baseline', 
                    gap: '12px',
                    marginBottom: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid var(--border-light)'
                  }}>
                    <span 
                      className="body-regular" 
                      style={{ 
                        textDecoration: 'line-through', 
                        color: 'var(--text-light)' 
                      }}
                    >
                      {offer.originalPrice}€
                    </span>
                    <span className="heading-1" style={{ color: '#C9A961' }}>
                      {offer.specialPrice}€
                    </span>
                  </div>

                  {/* Validité */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '20px'
                  }}>
                    <Calendar size={16} color="var(--text-secondary)" />
                    <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      Valable jusqu'au {offer.validUntil}
                    </span>
                  </div>

                  {/* CTA */}
                  <a 
                    href="https://www.planity.com/nina-care-institut-91360-villemoisson-sur-orge" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{ 
                      width: '100%',
                      background: offer.isHighlight ? '#C9A961' : 'transparent',
                      color: offer.isHighlight ? 'var(--bg-primary)' : 'var(--interactive-base)',
                      borderColor: offer.isHighlight ? '#C9A961' : 'var(--interactive-base)'
                    }}
                  >
                    Réserver l'offre
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos Produits - Excellence Naturelle */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="grid-two-column" style={{ alignItems: 'center', gap: '48px' }}>
            <div>
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '8px 16px',
                background: 'var(--bg-secondary)',
                marginBottom: '20px'
              }}>
                <Sparkles size={16} color="#C9A961" />
                <span className="body-small" style={{ color: '#C9A961', fontWeight: 700 }}>
                  EXCELLENCE NATURELLE
                </span>
              </div>
              
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Nos Produits
              </h2>
              
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '20px' }}>
                Chez Nina Care Institut, nous avons fait le choix de l'excellence naturelle. Chaque soin est réalisé avec une sélection rigoureuse de produits d'exception.
              </p>

              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%',
                    background: '#C9A961',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span style={{ color: 'var(--bg-primary)', fontSize: '16px', fontWeight: 700 }}>✓</span>
                  </div>
                  <p className="body-regular">
                    <strong>99% d'ingrédients naturels</strong> - Acide hyaluronique, Aloe Vera, extraits de blé et betterave
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%',
                    background: '#C9A961',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span style={{ color: 'var(--bg-primary)', fontSize: '16px', fontWeight: 700 }}>✓</span>
                  </div>
                  <p className="body-regular">
                    <strong>Bronzag'Expert 4ème génération</strong> - Formules 100% naturelles, fabriquées en France
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%',
                    background: '#C9A961',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span style={{ color: 'var(--bg-primary)', fontSize: '16px', fontWeight: 700 }}>✓</span>
                  </div>
                  <p className="body-regular">
                    <strong>Éthique & Qualité</strong> - Non testés sur les animaux, sans OGM, sans nanoparticules
                  </p>
                </div>
              </div>

              <Link 
                to="/excellence-naturelle" 
                className="btn-icon"
                style={{ display: 'inline-flex' }}
              >
                En savoir plus
                <ArrowRight size={16} />
              </Link>
            </div>

            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_beauty-institut-nina/artifacts/d4u4ioul_9DDC9875-A45A-4210-BB79-003216C6BDDB.png"
                alt="Produits naturels Nina Care"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  minHeight: '400px',
                  objectFit: 'cover',
                  border: '1px solid var(--border-light)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services en vedette */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Nos Services</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Des prestations sur mesure pour sublimer votre beauté
            </p>
          </div>

          <div className="grid-product-showcase">
            {featuredServices.map((service) => (
              <div key={service.id} className="product-card">
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="product-card-image"
                  style={{ width: '100%', height: '280px', objectFit: 'cover' }}
                />
                <div style={{ padding: '24px' }}>
                  <p className="body-small" style={{ color: '#C9A961', marginBottom: '8px', fontWeight: 700 }}>
                    {service.category}
                  </p>
                  <h3 className="product-card-title" style={{ marginBottom: '8px' }}>
                    {service.title}
                  </h3>
                  <p className="product-card-description" style={{ marginBottom: '16px' }}>
                    {service.description}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />
                      {service.duration}
                    </span>
                    <span className="heading-3" style={{ color: '#C9A961' }}>
                      {service.price}€
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/services" className="btn-icon">
              Voir tous nos services
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid-two-column">
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Pourquoi choisir Nina Care Institut ?
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                Depuis fin 2025, nous sommes dédiés à votre bien-être et à la mise en valeur de votre beauté naturelle.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '0px',
                    border: '1px solid var(--border-medium)',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Award size={24} color="#C9A961" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Expertise professionnelle</h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Des esthéticiennes qualifiées et passionnées à votre service
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '0px',
                    border: '1px solid var(--border-medium)',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Star size={24} color="#C9A961" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Produits de qualité</h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Sélection rigoureuse de produits pour des résultats visibles
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '0px',
                    border: '1px solid var(--border-medium)',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Clock size={24} color="#C9A961" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Soins personnalisés</h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Chaque prestation adaptée à vos besoins spécifiques
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_beauty-institut-nina/artifacts/ul01pi4f_E878FE7C-EEBB-483E-8F06-93E4FEBAEE7D.png"
                alt="Institut Nina Care"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  minHeight: '400px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Ils nous font confiance</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Découvrez les avis de nos clientes satisfaites
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth >= 768 ? 'repeat(3, 1fr)' : '1fr',
            gap: '32px'
          }}>
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                style={{
                  padding: '32px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  background: 'var(--bg-primary)'
                }}
              >
                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#C9A961" color="#C9A961" />
                  ))}
                </div>
                <p className="body-regular" style={{ marginBottom: '16px', fontStyle: 'italic' }}>
                  "{testimonial.comment}"
                </p>
                <div>
                  <p className="button-text" style={{ marginBottom: '4px' }}>{testimonial.name}</p>
                  <p className="body-small" style={{ color: 'var(--text-light)' }}>{testimonial.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section-padding" 
        style={{ 
          background: 'var(--bg-secondary)',
          textAlign: 'center'
        }}
      >
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
            Prête à vous offrir un moment de bien-être ?
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '40px' }}>
            Réservez votre rendez-vous en ligne en quelques clics sur Planity ou Treatwell
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="https://www.planity.com/nina-care-institut-91360-villemoisson-sur-orge" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Réserver sur Planity
            </a>
            <a 
              href="https://www.treatwell.fr/salon/nina-care-institut-1/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Réserver sur Treatwell
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
